import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-food-quizzes", title: "Why Food Quizzes?" },
  { id: "world-cuisine", title: "World Cuisine Quizzes" },
  { id: "food-science", title: "Food Science Trivia" },
  { id: "specialty-quizzes", title: "Specialty Food Quizzes" },
];

const quizResources = [
  { name: "Food Around the World", url: "https://www.britannica.com/quiz/food-around-the-world", description: "Global cuisine knowledge" },
  { name: "Ultimate Foodie Quiz", url: "https://www.britannica.com/quiz/ultimate-foodie-quiz", description: "Test your foodie credentials" },
  { name: "Food Safety Quiz", url: "https://play.howstuffworks.com/quiz/food-safety-quiz", description: "Kitchen safety knowledge" },
  { name: "Food Science Quiz", url: "https://www.britannica.com/quiz/food-science", description: "The science behind food" },
  { name: "Pizza Lovers Quiz", url: "https://www.proprofs.com/quiz-school/quizzes/pizza-lovers-quiz", description: "Are you a true pizza fan?" },
];

const relatedTopics = [
  { name: "Science Quizzes", path: "/quizzes/science-quizzes/", description: "Science and nature" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "General knowledge" },
  { name: "Fun Quizzes", path: "/quizzes/fun-quizzes-to-take/", description: "Entertainment quizzes" },
];

const FoodQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Food Quizzes: Test Your Culinary Knowledge"
      metaTitle="Food Quizzes | Culinary Trivia & Cooking Tests 2025"
      metaDescription="Challenge your food knowledge with quizzes on world cuisine, cooking techniques, food science, and culinary history. From pizza to sushi - prove you're a true foodie."
      keywords="food quizzes, cooking trivia, culinary quiz, world cuisine quiz, food science test, foodie quiz, pizza quiz"
      canonicalUrl="/quizzes/food-quizzes/"
      category="Food & Cooking"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="Food is more than just sustenance—it's culture, science, and art. Food quizzes test your knowledge of cuisines from around the world, cooking techniques, and the fascinating science behind what we eat."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-food-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Food Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Whether you're a home cook, a restaurant enthusiast, or just someone who loves to eat, food quizzes offer an appetizing way to learn about culinary traditions and food facts from around the world.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Discover world cuisines:</strong> Learn about dishes from every continent</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Learn food origins:</strong> Where did your favorite foods come from?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Understand food science:</strong> The chemistry of cooking</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Improve food safety:</strong> Learn proper kitchen practices</span>
          </li>
        </ul>
      </section>

      <section id="world-cuisine" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          World Cuisine <span className="text-gradient">Quizzes</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/food-around-the-world" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Food Around the World Quiz</a> challenges you to identify dishes, ingredients, and culinary traditions from different cultures.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Asian Cuisine</h4>
            <p className="text-sm text-muted-foreground">Sushi, dim sum, pho, and more</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">European Dishes</h4>
            <p className="text-sm text-muted-foreground">Italian, French, Spanish classics</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Latin American Food</h4>
            <p className="text-sm text-muted-foreground">Tacos, ceviche, empanadas</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Middle Eastern Cuisine</h4>
            <p className="text-sm text-muted-foreground">Falafel, hummus, shawarma</p>
          </div>
        </div>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://play.howstuffworks.com/quiz/can-you-match-the-food-to-its-country-of-origin" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Can You Match the Food to Its Country of Origin?</a> from HowStuffWorks is a popular quiz that tests whether you know where dishes like croissants, sushi, and tacos actually originated.
        </p>
      </section>

      <section id="food-science" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Food Science <span className="text-gradient">Trivia</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/food-science" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Food Science Quiz</a> dives into the chemistry and biology of food. Test your knowledge of:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Vitamins and nutrients:</strong> What makes food healthy?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Cooking chemistry:</strong> Why does bread rise? Why does meat brown?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Food preservation:</strong> How do we keep food fresh?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Food safety:</strong> Proper temperatures and handling</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://play.howstuffworks.com/quiz/food-science-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">The Ultimate Food Science Quiz</a> from HowStuffWorks challenges you on everything from why onions make you cry to the Maillard reaction that gives grilled food its flavor.
        </p>
      </section>

      <section id="specialty-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Specialty Food <span className="text-gradient">Quizzes</span>
        </h2>
        
        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Pizza Lovers Quiz</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://www.proprofs.com/quiz-school/quizzes/pizza-lovers-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">"Only True Pizza Lovers Will Be Able to Ace This Quiz"</a> from ProProfs tests your knowledge of pizza history, toppings, and regional styles. Do you know the difference between Neapolitan and New York style? What makes a Chicago deep dish unique?
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Menu Vocabulary</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/whats-on-the-menu-vocabulary-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">What's on the Menu? Vocabulary Quiz</a> tests whether you know culinary terms. What's a "julienne"? What does "al dente" mean? These quizzes are perfect for aspiring chefs and restaurant enthusiasts.
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Food Facts & Fiction</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://www.britannica.com/quiz/fact-or-fiction-your-favorite-foods" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Fact or Fiction: Your Favorite Foods</a> challenges common food myths. Did carrots really improve British pilots' night vision? Can you actually taste the difference between Pepsi and Coke? Separate food facts from folklore.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default FoodQuizzes;
